import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import { content, OmniAnalyticsService } from '@cigna/omni/shared';
import { playAudio } from '../input/util';
import { FeatureTogglesFacade, FeatureToggleType } from '@cigna/features';
import {
  CtaListItemData,
  MessageList,
  ParticipantEventListItem,
} from '../interfaces';
import { ConversationInterface } from '../interfaces/conversation.interface';
import { OutroObj } from '../interfaces/outro.interface';
import { CdnPrefixPipe } from '../pipes/cdn-prefix.pipe';
import { WaitTimeTypes } from '../wait-time/wait-time.component';
import { OmniConversationsFacade } from '@cigna/omni/conversations-state';
import { OmniCommChannelFacade } from '@cigna/omni/comm-channel-feature';
import isEqual from 'lodash/isEqual';
import { take } from 'rxjs/operators';
import { OmniChatFacade } from '@cigna/omni/chat-state';
@Component({
  selector: 'cigna-omni-messaging-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OmniMessagingListComponent implements AfterViewChecked, OnChanges {
  @Input()
  public messages: MessageList;
  @Input()
  public outroObj: OutroObj;
  @Input()
  public conversationType: WaitTimeTypes;
  @Input()
  public disclaimer: string;
  @Input()
  public conversation: ConversationInterface;
  @Input()
  public isLiveChatAvailable: boolean;
  @Input()
  public chatCloseHrsMsg: string;
  @Output()
  public messageViewed = new EventEmitter<string>();
  @Output()
  public sendCta = new EventEmitter<CtaListItemData>();
  @Output()
  public navigateBack = new EventEmitter();
  private mostRecentMessageId: string;
  public isTyping: boolean;
  private soundFileLink: string;
  @Input()
  public isUnmuted: boolean;
  public isEvernorth: boolean;
  public canUpdateAnalyticsData: boolean;
  @Input() public chatTitle: string;
  @Input() public shouldEnablePreChatWelcome: boolean;
  @Input() public shouldEnableNewChat: boolean;
  @Input() public isHistoryView: boolean;
  @Input() public isAiBot: boolean;
  public isHistoryLinkEnable: boolean;
  @Input() public isUseRightNowChat: boolean;
  @Input() public shouldEnabledBackToPrevious: boolean;
  @Input() public canReplaceBackToPreviousWithEndChat: boolean;
  @Input() public isUpdatingConversation: boolean;
  public agentLeftMessageCopy: string;
  public isTransferMessage: boolean;
  primaryTopicName: string;
  secondaryTopicName: string;
  private isAutoScrollAllowed: boolean;

  constructor(
    private elRef: ElementRef,
    private cdnPrefix: CdnPrefixPipe,
    private featureTogglesFacade: FeatureTogglesFacade,
    private analytics: OmniAnalyticsService,
    private conversationsFacade: OmniConversationsFacade,
    private commChannelFacade: OmniCommChannelFacade,
    private chatFacade: OmniChatFacade,
  ) {
    this.soundFileLink = this.cdnPrefix.transform('/assets/sounds/typing.mp3');
    this.featureTogglesFacade
      .featuresEnabled(['isEvernorthEnabled'])
      .pipe(take(2))
      .subscribe((isEvm) => {
        this.isEvernorth = isEvm;
      });

    this.featureTogglesFacade
      .featuresEnabled(['canUpdateAnalyticsData'])
      .pipe(take(2))
      .subscribe((canUpdateAnalyticsData) => {
        this.canUpdateAnalyticsData = canUpdateAnalyticsData;
      });
  }

  public trackByMostRecent(_i: any, message: any): string | void {
    if (message && message._id) {
      return `${message._id}${message.mostRecent ? 'mostRecent' : ''}`;
    }
  }

  public playAudioOnAgentJoining(
    last: ParticipantEventListItem,
    previousValue: MessageList,
  ): void {
    const doesIncludeCopy =
      previousValue?.length &&
      Object.keys(previousValue.slice(-1)[0])?.includes('copy');
    const hasClosedChat = this.verifyTimeStampForCloseChat();

    if (
      last?.copy &&
      !doesIncludeCopy &&
      last?.copy.includes(content.agentJoinMessage)
    ) {
      if (this.isUnmuted) {
        playAudio(this.soundFileLink);
      }
      this.conversationsFacade.agentJoinChat();
    }

    if (
      (this.conversation?.state as string) === 'closed' &&
      last?.copy &&
      !doesIncludeCopy &&
      hasClosedChat &&
      last?.copy.includes(content.agentLeftMessage)
    ) {
      this.agentLeftMessageCopy = last?.copy;
      this.conversationsFacade.setCloseOrExitFlag(false);
      this.isHistoryLinkEnable = false;
      if (!this.canUpdateAnalyticsData) {
        this.analytics.trackEvent('chatEndByAgent', {});
      }
    }
  }

  verifyTimeStampForCloseChat() {
    const createdDate = this.conversation?.events
      ? this.conversation?.events?.slice(-1)[0].created
      : '';
    return new Date(createdDate) > new Date(Date.now() - 5000);
  }

  playAudioOnAgentsMessage() {
    if (
      this.isUnmuted &&
      this.conversation.newMessages &&
      this.conversation.lastMessageBy?.role.toLocaleLowerCase() === 'agent'
    ) {
      playAudio(this.soundFileLink);
    }
  }

  public checkIsAgentTyping() {
    if (this.conversation.events) {
      const [lastConvoEvent] = this.conversation.events?.slice(-1);
      this.isTyping =
        lastConvoEvent?.type === 'activity' &&
        lastConvoEvent?.activityType === 'responding';
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public ngOnChanges(changes: ComponentChanges<this>): void {
    if (
      (this.conversation?.state as string) === 'transfer-manual' ||
      (this.isAiBot && this.conversation?.state !== 'closed')
    ) {
      this.featureTogglesFacade.updateFeatureToggles(
        { enableInput: true },
        FeatureToggleType.PERMISSION,
      );
    } else {
      this.featureTogglesFacade.updateFeatureToggles(
        { enableInput: false },
        FeatureToggleType.PERMISSION,
      );
    }

    if (changes.messages && changes.messages.currentValue) {
      const [last] = changes.messages.currentValue.slice(-1);
      const id = (last && last._id) || null;
      const previousValue = changes.messages.previousValue;
      if (id && id !== this.mostRecentMessageId) {
        this.messageViewed.emit(id);
        this.mostRecentMessageId = id;
      }
      this.scrollUpdate();
      this.playAudioOnAgentJoining(
        last as ParticipantEventListItem,
        previousValue,
      );
    }

    if (
      this.isUnmuted &&
      changes.outroObj?.currentValue?.show &&
      !changes.outroObj?.previousValue?.show &&
      this.verifyTimeStampForCloseChat()
    ) {
      playAudio(this.soundFileLink);
      this.scrollUpdate();
    }

    if (changes.conversation?.currentValue) {
      if (
        !isEqual(
          changes.conversation.currentValue.events?.filter(
            (event) => event.type === 'message',
          ),
          changes.conversation.previousValue?.events?.filter(
            (event) => event.type === 'message',
          ),
        )
      ) {
        this.playAudioOnAgentsMessage();
      }
      if (
        !isEqual(
          changes.conversation.currentValue.events,
          changes.conversation.previousValue?.events,
        )
      ) {
        this.isAutoScrollAllowed = true;
        this.scrollUpdate();
        this.updateTopicNames();
        this.checkIsAgentTyping();
      }
    }
    if (
      changes.isLiveChatAvailable &&
      !changes.isLiveChatAvailable.currentValue &&
      this.conversation?.type === 'bot' &&
      !this.shouldEnablePreChatWelcome
    ) {
      this.navigateBack.emit();
    }
    if (
      !changes.isLiveChatAvailable?.previousValue &&
      changes.isLiveChatAvailable?.currentValue &&
      !changes.isLiveChatAvailable.firstChange &&
      this.shouldEnablePreChatWelcome &&
      !this.conversation
    ) {
      this.commChannelFacade.interact(
        {
          buttonText: 'Chat with a Guide',
          buttonId: 'chatGuide',
        },
        { tags: this.isUseRightNowChat ? ['advocate', 'member'] : ['default'] },
      );
    }

    const transferEventManualIndex = this.conversation?.events?.findIndex(
      (event) => event.state === 'transfer-manual',
    );
    const transferEventIndex = this.conversation?.events?.findIndex(
      (event) => event.state === 'transfer',
    );
    const transferEventWithMessage =
      transferEventManualIndex > -1
        ? this.conversation?.events
            ?.slice(transferEventManualIndex)
            .filter((event) => event.type === 'message')
        : [];

    if (
      (transferEventWithMessage?.length > 1 || transferEventIndex > -1) &&
      (this.conversation?.state as string) !== 'closed' &&
      !this.isHistoryView
    ) {
      this.conversationsFacade.setCloseOrExitFlag(true);
      this.isHistoryLinkEnable = true;
    }
    if (
      (this.conversation?.state as string) === 'closed' &&
      !this.isHistoryView
    ) {
      this.conversationsFacade.setCloseOrExitFlag(false);
      this.isHistoryLinkEnable = false;
    }
  }

  updateTopicNames() {
    const messageEvents = this.conversation?.events?.filter(
      (event) => event.type === 'message',
    );
    this.primaryTopicName = messageEvents?.[1]?.message || '';

    this.secondaryTopicName = messageEvents?.[3]?.message || '';
  }

  public sendCtaFn(data: CtaListItemData) {
    this.sendCta.emit({
      ...data,
      topicId:
        this.conversationType === 'hil'
          ? 'nurseTopic'
          : this.secondaryTopicName
          ? 'chatMinibotTopic'
          : this.primaryTopicName
          ? 'agentSecondaryTopic'
          : 'agentPrimaryTopic',
      primaryTopicName: this.primaryTopicName,
    });
    if (this.isEvernorth) {
      const topicLabel = data.value ? data.value : '';
      this.analytics.trackEvent('trackPrimaryTopicClick', { topicLabel });
    }
  }

  public backToPrevious() {
    const paramTags = this.isUseRightNowChat
      ? ['advocate', 'member']
      : ['default'];

    if (this.canReplaceBackToPreviousWithEndChat) {
      this.conversationsFacade.endConversation(this.conversation?._id);
      this.chatFacade.closeDialog();
    } else {
      this.conversationsFacade.backToPrevious(this.conversation?._id, {
        tags: paramTags,
      });
    }
    this.conversationsFacade.setHeaderFocus(true);
  }

  scrollUpdate() {
    this.elRef.nativeElement.scrollTop = this.elRef.nativeElement.scrollHeight;
  }

  minibotLinkClick(linkName: string) {
    if (!this.canUpdateAnalyticsData) {
      this.analytics.trackEvent('chatMinibotLinkClick', {
        linkName,
        prop20: this.primaryTopicName as string,
      });
    }
  }

  ngAfterViewChecked() {
    if (this.isAutoScrollAllowed) {
      this.scrollUpdate();
      this.isAutoScrollAllowed = false;
    }
  }
}
